/**
 * Finishing an account — the stages after the password.
 *
 * The password is set on the link (`set-password.tsx`). What is left is the
 * two things only the person themselves can hold: a second factor if their
 * role requires one (§G.2), and a PIN if they will clock in on a crew iPad
 * (§G.3).
 *
 * Which stages they see comes from their roles, on `/me`, through
 * `stepsAfterPassword` — the same function the password page ran over the
 * link, so the bars here continue the bars there (Aaron, step 27). A crew
 * member with no PIN can sign in and see the day and never clock in — so this
 * runs before the app does, rather than being somewhere in a settings menu
 * they will not open.
 */
import { useCallback, useState } from 'react'
import { stepsAfterPassword, type MeResponse, type SetupStep } from '@rowted/shared'
import { PinPad } from '../components/overlays.js'
import { Button } from '../components/primitives.js'
import { SetupProgress } from '../components/setup-progress.js'
import { TwoStepSetup } from '../components/two-step-setup.js'
import { TenantAccountApi } from '../account/providers.js'
import { RowtedLogo } from '../frame/logo.js'
import { request } from '../lib/api.js'

/** The stages this person still owes, in the order they are asked for. */
export function remainingSteps(me: MeResponse): Exclude<SetupStep, 'password'>[] {
  return stepsAfterPassword(me.user)
}

export function SetUp({
  me, onDone, onSkip, afterPassword = false,
}: {
  me: MeResponse
  onDone: () => void
  /** Offered when the account already works — see `App.tsx`. */
  onSkip?: () => void
  /**
   * Arrived from the password page, so the password is drawn as the stage
   * just finished. The prompt an existing account meets in `App.tsx` passes
   * nothing: nobody there has just chosen a password.
   */
  afterPassword?: boolean
}) {
  const [steps] = useState(() => remainingSteps(me))
  const [index, setIndex] = useState(0)
  /*
   * Once two-step is on, the recovery codes have been on screen, shown exactly
   * once. Leaving "later" there would let somebody walk away from a flow that
   * has already committed them.
   */
  const [committed, setCommitted] = useState(false)
  // Stable for the same reason `MfaStep`'s `changed` is — see there.
  const commit = useCallback(() => setCommitted(true), [])
  const step = steps[index]
  const stages: SetupStep[] = afterPassword ? ['password', ...steps] : steps

  function next() {
    if (index + 1 >= steps.length) onDone()
    else setIndex(index + 1)
  }

  if (!step) {
    onDone()
    return null
  }

  return (
    <div className="flex min-h-app items-center justify-center bg-bg p-4">
      <div className="flex w-full max-w-[420px] flex-col gap-4 rounded-card border border-line bg-surface p-6">
        <RowtedLogo />

        <SetupProgress
          company={me.tenant.name}
          steps={stages}
          at={index + (afterPassword ? 1 : 0)}
        />

        {step === 'mfa'
          ? <MfaStep onDone={next} onCommitted={commit} />
          : <PinStep userId={me.user.id} onDone={next} />}

        {onSkip && !committed && (
          <button
            type="button"
            onClick={onSkip}
            className="text-cell text-text-muted underline underline-offset-2 hover:text-text"
          >
            Set this up later
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Two-step, through the same dialog My profile uses (Aaron, step 27).
 *
 * This posted to `/auth/mfa/enroll` and `/auth/mfa/confirm` — an
 * authenticator-only flow from before two-step became a list of methods, both
 * answering **410** since — so every invite that reached this stage failed
 * with "a POST error" and nothing else to go on. `TwoStepSetup` is the one
 * place a method is set up: four kinds, the recovery codes the first time,
 * and the step-up it needs answered by the password page having just proved
 * it (`markSteppedUp` in `/auth/set-password`).
 *
 * The stage is drawn first and the dialog is one press away, not open on
 * arrival: open, it covered the card — and with it the stage bars this page
 * exists to show, so the second screen of the flow said nothing about where
 * the person was.
 */
function MfaStep({ onDone, onCommitted }: { onDone: () => void; onCommitted: () => void }) {
  const [open, setOpen] = useState(false)
  const [enrolled, setEnrolled] = useState(false)

  /*
   * Stable, because `TwoStepSetup` reloads whenever its `onChanged` changes —
   * an arrow written inline here would reload it on every render, for ever.
   */
  const changed = useCallback((answer: { methods: unknown[] }) => {
    if (answer.methods.length > 0) {
      setEnrolled(true)
      onCommitted()
    }
  }, [onCommitted])

  return (
    <TenantAccountApi>
      <div>
        <h1 className="m-0 text-title">Two-step sign-in</h1>
        <p className="m-0 mt-1 text-cell leading-[1.45] text-text-muted">
          {enrolled
            ? 'You’re set. Rowted will ask for it when you sign in and before sensitive '
              + 'changes. Add a second way from My profile whenever you like.'
            : 'Your role asks for a second step when you sign in — a passkey, an '
              + 'authenticator app, or a code by email or text. Pick one now.'}
        </p>
      </div>

      {enrolled
        ? <Button variant="primary" size="big" onClick={onDone}>Continue</Button>
        : (
          <Button variant="primary" size="big" onClick={() => setOpen(true)}>
            Choose a way to sign in
          </Button>
        )}

      {open && (
        <TwoStepSetup
          onClose={() => {
            setOpen(false)
            if (enrolled) onDone()
          }}
          onChanged={changed}
        />
      )}
    </TenantAccountApi>
  )
}

function PinStep({ userId, onDone }: { userId: string; onDone: () => void }) {
  const [pin, setPin] = useState('')
  const [confirming, setConfirming] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit() {
    // Typed twice, because a PIN that was mistyped once is a PIN nobody can
    // clock in with, on a shared iPad, at 6:45 in the morning.
    if (confirming === null) {
      if (pin.length < 4) return
      setConfirming(pin)
      setPin('')
      return
    }
    if (pin !== confirming) {
      setPin('')
      setConfirming(null)
      setError('Those did not match. Start again.')
      return
    }

    setBusy(true)
    try {
      await request(`/people/${userId}/pin`, { method: 'PUT', body: { pin } })
      onDone()
    } catch (cause) {
      setPin('')
      setConfirming(null)
      setError(cause instanceof Error ? cause.message : 'That PIN could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div>
        <h1 className="m-0 text-title">
          {confirming === null ? 'Pick a PIN' : 'Type it again'}
        </h1>
        <p className="m-0 mt-1 text-cell text-text-muted">
          {confirming === null
            ? 'Four to six digits. This is how you clock in and out on the crew iPad — don’t share it, it’s your timesheet.'
            : 'Once more, so a slip now does not lock you out at 6:45 in the morning.'}
        </p>
      </div>

      <div className="flex justify-center">
        <PinPad value={pin} onChange={setPin} onSubmit={() => { if (!busy) void submit() }} />
      </div>

      {error && (
        <p role="alert" className="m-0 rounded-button bg-danger-bg px-3 py-2 text-cell text-danger">
          {error}
        </p>
      )}

      <p className="m-0 text-sub text-text-faint">
        Forget it later? Your crew lead can reset it from the iPad.
      </p>
    </>
  )
}
